import React, { useEffect, useState } from 'react';
import { Text } from '../components/Text';
import { Header } from '../components/Header';
import LoaderButton from '../components/LoaderButton';
import { useUser } from '../context/User';
import { generateMfaQrCode, verifyMfaQrCode } from '../libs/db-lib';
import { toast } from 'react-toastify';
import { Loading } from '../components/Loading';
import { ChildProps } from '../types';

const MfaSetup = (props: ChildProps) => {  
  const { user } = useUser();

  const [isLoadingQrCode, setIsLoadingQrCode] = useState<boolean>(true);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);

  const [qrCode, setQrCode] = useState<string>('');
  const [secretCode, setSecretCode] = useState<string>('');

  const [isEnteringTotp, setIsEnteringTotp] = useState<boolean>(false);
  const [totp, setTotp] = useState<string>('');

  useEffect(() => {
    const handleGetMfaQrCode = async () => {
      setIsLoadingQrCode(true);
      
      if (user) {
        const response = await generateMfaQrCode(user?.userName);

        if (!response.error) {
          setIsLoadingQrCode(false);
          setQrCode(response.qrCode);
          setSecretCode(response.secretCode);
        } else {
          toast.error('Error generating QR code!', {
            containerId: 'standard',
          });
        }
      }
    };

    if (!user?.mfaEnabled) {
      handleGetMfaQrCode();
    }
  }, [user]);

  const handleSubmitTotp = async () => {
    setIsLoadingSubmit(true);
    if ((totp || '').replace(/ /g, '').length !== 6) {
      toast.error('Invalid One-time password code!', {
        containerId: 'standard',
      });
    } else {
      const response = await verifyMfaQrCode(user?.userName, totp);

      if (!response.error) {
        toast.success('Successfully enabled Multi-Factor Authentication! Returning to login.', {
          containerId: 'standard',
        });

        setTimeout(() => {
          props.logout();
        }, 1575);
      } else {
        toast.error(`Failed to enable Multi-Factor Authentication: ${response?.error}`, {
          containerId: 'standard',
        });
      }
    }
    setIsLoadingSubmit(false);
  };

  return (
    <>
      <Header title="MFA Setup" />

      <div className="l-container">
        {!isEnteringTotp ?
          <>
            <div className='u-margin-bottom-xlarge'>
              Before continuing, you <span className='u-font-weight-bold'>MUST</span> enroll in Multi-Factor Authentication (MFA). Scan the QR code below using your preferred authenticator app:
            </div>

            <div className='l-container-center u-margin-bottom-xlarge'>
              {isLoadingQrCode ? (
                  <Loading className="loading-image" />
                ) : (
                  <img src={qrCode} />
                )}
            </div>

            <div className='u-margin-top-xlarge u-margin-bottom-small'>
              If you are unable to scan the QR code, manually enter the following code into your preferred authenticator app: {secretCode}
            </div>

            <div className='u-margin-bottom-xlarge'>
              After successfully adding the account in your authenticator app, click 'Next' to proceed.
            </div>
            
            <div className='l-container-center'>
              <button
                className="c-btn"
                onClick={() => setIsEnteringTotp(true)}
                disabled={!user || isLoadingQrCode || !(qrCode || '')?.length || !(secretCode || '')?.length}
              >
                Next
              </button>
            </div>
          </>
          :
          <>
            <div className='u-margin-bottom'>
              Please enter the One-Time Password (OTP) code below from your chosen authenticator app. After successful enrollment, you will be returned to the login page and prompted to reset your password.
            </div>
            <div className="c-field">
              <label htmlFor="One-Time Password" className="c-field__label u-is-vishidden">
                One-Time Password
              </label>
              <input
                type="text"
                id="One-Time Password"
                maxLength={50}
                className="c-input u-font-mono l-container-center u-width-50-percent"
                placeholder="One-Time Password"
                disabled={!user || isLoadingQrCode || isLoadingSubmit}
                value={totp}
                onChange={(event) => setTotp(event.target.value)}
              />
            </div>
            <div className='l-container-center'>
              <button
                className="c-btn-outline"
                onClick={() => setIsEnteringTotp(false)}
                disabled={!user || isLoadingQrCode || isLoadingSubmit}
              >
                Back
              </button>
              <LoaderButton
                className="c-btn u-margin-left-large"
                disabled={!user || isLoadingQrCode || isLoadingSubmit || totp.length !== 6}
                text="Submit"
                loadingText="Submitting..."
                onClick={() => handleSubmitTotp()}
              />
            </div>
          </>
        }
      </div>
    
    </>
  );
}

export default (props: ChildProps) => {
  return <MfaSetup {...props} />;
};